import { useState } from 'react'
import { Drawer } from 'antd'
import { useTranslation } from 'react-i18next'
import { Domiciliation } from 'globals/models/Domiciliation'
import Mail, { MailType } from 'globals/models/Mail'
import { TFunction } from 'i18next'
import * as Yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { RcFile } from 'antd/lib/upload/interface'
import GraphQLHandler from 'components/GraphQLHandler'
import { useMutation } from '@apollo/client'
import { CREATE_MAIL, CreateMailResult, CreateMailVariables } from 'pages/AddMail/mutations'
import AddMailForm from './components/AddMailForm'
import AddMailConfirmation from 'pages/AddMail/components/AddMailConfirmation'
import { TestFunction } from 'yup'
import { SetState } from 'globals'
import { displayErrors, DoMutation } from 'globals/graphql'
import moment, { Moment } from 'moment'
import { UserMailsResult, UserMailsVariables } from 'graphql/mails/queries'
import { UPDATE_MAIL } from 'pages/ClientDetails/components/MailDetails/mutations'

export interface IAddMailForm {
  type: MailType
  sender?: string
  receiptDate?: string | Moment
  weight?: number
  scannedFile?: RcFile
}

interface IProps {
  visible: boolean
  onClose: () => void
  selectedMail?: Mail | null
  domiciliation?: Domiciliation
  isFromUpdateMail?: boolean | undefined
  setSelectedMail?: SetState<Mail | null>
  setIsFromUpdateMail?: SetState<boolean | undefined>
  refetch?: () => void
}

const AddMail = (props: IProps) => {
  const {
    visible,
    onClose,
    domiciliation,
    isFromUpdateMail,
    selectedMail,
    setSelectedMail,
    setIsFromUpdateMail,
    refetch
  } = props
  const { t } = useTranslation()
  const [formSent, setFormSent] = useState(false)
  const [mailId, setMailId] = useState<number>()
  const [addError, setAddError] = useState<string>()

  const initialValues: IAddMailForm = {
    type: selectedMail ? selectedMail.mailType : 'courrier',
    receiptDate: selectedMail ? moment(selectedMail.dateOfReceipt) : moment()
  }

  const validation = (t: TFunction, domiciliation: Domiciliation): Yup.SchemaOf<IAddMailForm> =>
    Yup.object({
      type: Yup.mixed<MailType>()
        .oneOf(['courrier', 'delivery_notice', 'ar', 'colis'], t('global.error.invalid', { field: t('mails.type') }))
        .required(t('global.error.required', { field: t('mails.type') })),
      sender: Yup.string(),
      receiptDate: Yup.string().required(t('global.error.required-f', { field: t('mails.receipt-date-form') })),
      weight: Yup.number()
        .test('weight-test', t('global.error.required', { field: t('mails.weight') }), validateWeight)
        .typeError(t('global.error.invalid', { field: t('mails.weight') })),
      scannedFile: Yup.mixed<RcFile>()
        .test(
          'scanned-file-test',
          t('global.error.required-f', { field: t('mails.add.digital-file') }),
          validateScannedFile(domiciliation)
        )
        .test('scanned-file-size-test', t('global.error.file-size', { weight: '5Mo' }), validateScannedFileSize)
    })

  const onUpdateMail =
    (doUpdateMail, setFormSent: SetState<boolean>) =>
    (variables: IAddMailForm, helpers: FormikHelpers<IAddMailForm>) => {
      selectedMail &&
        doUpdateMail({
          variables: {
            userMailId: selectedMail.id,
            mailType: variables.type,
            sendName: variables.sender,
            weight: Number(variables.weight),
            dateOfReceipt: variables.receiptDate,
            scanFile: variables.scannedFile
          }
        })
          .then(() => {
            helpers.resetForm()
            setFormSent(true)
          })
          .catch((res: { data }) => {
            setAddError(res?.data?.error?.message)
          })
    }

  const onAddMail =
    (
      doCreateMail: DoMutation<CreateMailResult, CreateMailVariables>,
      domiciliation: Domiciliation,
      setFormSent: SetState<boolean>,
      setMailId: SetState<number | undefined>,
      setAddError: SetState<string | undefined>
    ) =>
    (variables: IAddMailForm, helpers: FormikHelpers<IAddMailForm>) => {
      doCreateMail({
        variables: {
          domiciliationId: domiciliation.id,
          mailType: variables.type,
          sendName: variables.sender,
          weight: Number(variables.weight),
          dateOfReceipt: variables.receiptDate,
          scanFile: variables.scannedFile
        }
      })
        .then((res) => {
          setTimeout(onCloseDialog(onClose), 5000)
          const returnedUserMail = res.data?.userMailCreate?.userMail
          helpers.resetForm()
          setFormSent(true)
          setMailId(returnedUserMail?.id)
          refetch && refetch()
        })
        .catch((res) => {
          setAddError(res?.data?.error?.message)
        })
    }

  const clearForm = (resetForm: () => void) => () => {
    resetForm()
    setFormSent(false)
    setMailId(undefined)
    setAddError(undefined)
    setSelectedMail && setSelectedMail(null)
    setIsFromUpdateMail && setIsFromUpdateMail(false)
  }

  const onCloseDialog = (onClose: () => void) => () => {
    onClose()
    setFormSent(false)
    setMailId(undefined)
    setAddError(undefined)
    setSelectedMail && setSelectedMail(null)
    setIsFromUpdateMail && setIsFromUpdateMail(false)
  }

  const validateScannedFile =
    (domiciliation: Domiciliation): TestFunction<RcFile | undefined> =>
    (value?: RcFile, context?) => {
      if (
        (domiciliation.userMailOption === 'scanning' ||
          domiciliation.userMailOption === 'scanning_and_destroy' ||
          domiciliation.userMailOption === 'scanning_and_forwarding' ||
          domiciliation.userMailOption === 'scanning_and_unrestricted_forwarding') &&
        !value &&
        (context?.parent as IAddMailForm).type !== 'colis' &&
        !selectedMail?.scanFile
      ) {
        return false
      }

      return true
    }

  const validateScannedFileSize: TestFunction<RcFile | undefined> = (value?: RcFile) => {
    if (value && value.size > 5000000) {
      return false
    }

    return true
  }

  const validateWeight: TestFunction<number | undefined> = (value: number | undefined, context) => {
    if ((context.parent as IAddMailForm).type === 'colis' && !value && selectedMail?.mailType !== 'colis') {
      return false
    }

    return true
  }

  return (
    <GraphQLHandler
      mutations={{
        createMail: useMutation<CreateMailResult, CreateMailVariables>(CREATE_MAIL, displayErrors),
        updateMail: useMutation<UserMailsResult, UserMailsVariables>(UPDATE_MAIL, displayErrors)
      }}
    >
      {({
        mutations: {
          createMail: [doCreateMail, createMailData],
          updateMail: [doUpdateMail, updateMailData]
        }
      }) =>
        domiciliation && (
          <Formik
            initialValues={initialValues}
            validationSchema={validation(t, domiciliation)}
            onSubmit={
              isFromUpdateMail
                ? onUpdateMail(doUpdateMail, setFormSent)
                : onAddMail(doCreateMail, domiciliation, setFormSent, setMailId, setAddError)
            }
            isInitialValid={validation(t, domiciliation).isValidSync(initialValues)}
            enableReinitialize={true}
          >
            {({ values, resetForm }) => (
              <Drawer visible={visible} width={320} onClose={onCloseDialog(onClose)}>
                {domiciliation?.status === 'canceled' && (
                  <AddMailConfirmation
                    domiciliation={domiciliation}
                    onClearForm={clearForm(resetForm)}
                    onClose={onCloseDialog(onClose)}
                    error={t('mails.add.closed-client')}
                  />
                )}

                {domiciliation?.status === 'pending_partner_check' && (
                  <AddMailConfirmation
                    domiciliation={domiciliation}
                    onClearForm={clearForm(resetForm)}
                    onClose={onCloseDialog(onClose)}
                    error={t('mails.add.waiting-client')}
                  />
                )}

                {domiciliation?.status !== 'canceled' && !formSent && (
                  <AddMailForm
                    domiciliation={domiciliation}
                    values={values}
                    createMailData={createMailData || updateMailData}
                    selectedMail={selectedMail}
                  />
                )}

                {formSent && (
                  <AddMailConfirmation
                    domiciliation={domiciliation}
                    onClearForm={clearForm(resetForm)}
                    onClose={onCloseDialog(onClose)}
                    error={addError}
                    mailId={mailId}
                    selectedMail={selectedMail}
                  />
                )}
              </Drawer>
            )}
          </Formik>
        )
      }
    </GraphQLHandler>
  )
}

export default AddMail
